import * as React from 'react';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';

export type CommissionReportParamsMode = 'reports' | 'summary' | 'range';

export interface ICommissionReportParamsSubmit {
    mode: CommissionReportParamsMode;
    reportBy?: string;
    periodNo?: number;
    begPeriod?: number;
    endPeriod?: number;
    mgrNo?: number;
    clNo?: number;
    includeDayRate: boolean;
}

interface ICommissionReportParamsDialogProps {
    dialogName: string;
    hideDialog: () => void;
    mode: CommissionReportParamsMode;
    onSubmit: (params: ICommissionReportParamsSubmit) => void;
    // The Period No. already entered in the toolbar - defaults the period field(s) below so the
    // user doesn't have to retype it, matching HistPerfBonusDialog's defaultPeriodNo pattern.
    defaultPeriodNo?: number;
    // 'range' mode only - mirrors oid 266's own :range_parms.beg_period/:end_period copy from
    // whatever the user already typed into PP_Range, rather than reusing defaultPeriodNo (which
    // would force beg and end to the same value).
    defaultBegPeriod?: number;
    defaultEndPeriod?: number;
}

// RPT_TYPE's own 2 static List Elements (oid 166/167) - "By Prospect"/P, "By Manager"/M.
const REPORT_BY_OPTIONS = [
    { value: 'P', label: 'By Prospect' },
    { value: 'M', label: 'By Manager' },
];

const labelStyle: React.CSSProperties = { whiteSpace: 'nowrap', width: '10rem' };
const fieldStyle: React.CSSProperties = { width: '100%' };

// Explicit useForm() generic - a narrower inline defaultValues object (just includeDayRate/
// reportBy) makes react-hook-form infer getValues()'s type from that literal alone, so every
// other registered field (periodNo/begPeriod/endPeriod/mgrNo/clNo) reads as a TS2339 error.
interface ICommissionReportParamsFormValues {
    reportBy: string;
    periodNo: string;
    begPeriod: string;
    endPeriod: string;
    mgrNo: string;
    clNo: string;
    includeDayRate: boolean;
}

// Shared by "Reports" (mirrors EXPENSE_PARAMS - Report By + a single Pay Period), "Summary Report"
// (mirrors REPORT_PARAMS - a Beginning/Ending Pay Period range, no Report By), and "Margins
// Report" (mirrors RANGE_PARMS, reached via PP_Range's own "Reports" button - Report By + a
// Beginning/Ending Pay Period range, but NO Manager Number field, unlike the other two) - all
// three real screens otherwise share the same Client Number/Include Day Rate? fields, so this is
// one dialog with `mode` swapping only the period/report-by/manager fields.
const CommissionReportParamsDialog = (props: ICommissionReportParamsDialogProps) => {
    const { register, errors, getValues } = useForm<ICommissionReportParamsFormValues>({ defaultValues: { includeDayRate: true, reportBy: 'P' } });

    const handleOk = () => {
        const values = getValues();
        props.onSubmit({
            mode: props.mode,
            reportBy: props.mode === 'reports' || props.mode === 'range' ? values.reportBy : undefined,
            periodNo: props.mode === 'reports' && values.periodNo ? Number(values.periodNo) : undefined,
            begPeriod: (props.mode === 'summary' || props.mode === 'range') && values.begPeriod ? Number(values.begPeriod) : undefined,
            endPeriod: (props.mode === 'summary' || props.mode === 'range') && values.endPeriod ? Number(values.endPeriod) : undefined,
            mgrNo: props.mode !== 'range' && values.mgrNo ? Number(values.mgrNo) : undefined,
            clNo: values.clNo ? Number(values.clNo) : undefined,
            includeDayRate: !!values.includeDayRate,
        });
    };

    return (
        <Dialog
            title={props.mode === 'reports' ? 'Reports' : props.mode === 'summary' ? 'Summary Report' : 'Margins Report'}
            dialogName={props.dialogName}
            isExitButtonInvisible={true}
            bodyStyle={{ width: '28%' }}
        >
            <form>
                {(props.mode === 'reports' || props.mode === 'range') && (
                    <RHFSelect
                        label="Report By"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportBy"
                        options={REPORT_BY_OPTIONS}
                        defaultVal="P"
                        register={register}
                        style={fieldStyle}
                    />
                )}
                {props.mode === 'reports' && (
                    <div style={{ marginTop: '.75rem' }}>
                        <RHFInputBox
                            label="Pay Period"
                            labelOnLeft={true}
                            labelStyle={labelStyle}
                            name="periodNo"
                            type="number"
                            register={register}
                            errors={errors}
                            style={fieldStyle}
                            defaultVal={props.defaultPeriodNo !== undefined ? String(props.defaultPeriodNo) : undefined}
                        />
                    </div>
                )}
                {props.mode === 'summary' && (
                    <>
                        <RHFInputBox
                            label="Beginning Pay Period"
                            labelOnLeft={true}
                            labelStyle={labelStyle}
                            name="begPeriod"
                            type="number"
                            register={register}
                            errors={errors}
                            style={fieldStyle}
                            defaultVal={props.defaultPeriodNo !== undefined ? String(props.defaultPeriodNo) : undefined}
                        />
                        <div style={{ marginTop: '.75rem' }}>
                            <RHFInputBox
                                label="Ending Pay Period"
                                labelOnLeft={true}
                                labelStyle={labelStyle}
                                name="endPeriod"
                                type="number"
                                register={register}
                                errors={errors}
                                style={fieldStyle}
                                defaultVal={props.defaultPeriodNo !== undefined ? String(props.defaultPeriodNo) : undefined}
                            />
                        </div>
                    </>
                )}
                {props.mode === 'range' && (
                    <>
                        <div style={{ marginTop: '.75rem' }}>
                            <RHFInputBox
                                label="Beginning Period"
                                labelOnLeft={true}
                                labelStyle={labelStyle}
                                name="begPeriod"
                                type="number"
                                register={register}
                                errors={errors}
                                style={fieldStyle}
                                defaultVal={props.defaultBegPeriod !== undefined ? String(props.defaultBegPeriod) : undefined}
                            />
                        </div>
                        <div style={{ marginTop: '.75rem' }}>
                            <RHFInputBox
                                label="Ending Period"
                                labelOnLeft={true}
                                labelStyle={labelStyle}
                                name="endPeriod"
                                type="number"
                                register={register}
                                errors={errors}
                                style={fieldStyle}
                                defaultVal={props.defaultEndPeriod !== undefined ? String(props.defaultEndPeriod) : undefined}
                            />
                        </div>
                    </>
                )}
                {props.mode !== 'range' && (
                    <div style={{ marginTop: '.75rem' }}>
                        <RHFInputBox label="Manager Number" labelOnLeft={true} labelStyle={labelStyle} name="mgrNo" type="number" register={register} errors={errors} style={fieldStyle} />
                    </div>
                )}
                <div style={{ marginTop: '.75rem' }}>
                    <RHFInputBox label="Client Number" labelOnLeft={true} labelStyle={labelStyle} name="clNo" type="number" register={register} errors={errors} style={fieldStyle} />
                </div>
                <div style={{ marginTop: '.75rem' }}>
                    <RHFCheckbox label="Include Day Rate ?" labelOnLeft={true} labelStyle={{ ...labelStyle, marginRight: '.5rem' }} name="includeDayRate" register={register} defaultChecked={true} />
                </div>
            </form>
            <div className="form-row" style={{ marginTop: '1.5rem', justifyContent: 'center' }}>
                <Button className="blue-hover-button" style={{ width: '25%', marginRight: '1rem' }} text="OK" onClick={handleOk} />
                <Button className="blue-hover-button" style={{ width: '25%' }} text="Cancel" onClick={() => props.hideDialog()} />
            </div>
        </Dialog>
    );
};

export default CommissionReportParamsDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/CommissionReportParamsDialog.tsx